import React from "react";
import { MessageCircle, ChevronDown, History } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui-kit";
import { fmtDay, fmtDateTime } from "@/lib/time";
import { usePrefs } from "@/contexts/PrefsContext";
import type { Booking, WaTemplate } from "@/types";

const waNumber = (p: string) => {
  let d = p.replace(/\D/g, "");
  if (d.startsWith("00")) d = d.slice(2);
  if (d.startsWith("07")) d = "964" + d.slice(1);
  return d;
};

const guestsLabel = (n: number, lang: string) =>
  lang === "ar" ? (n === 1 ? "شخص واحد" : n === 2 ? "شخصين" : n <= 10 ? `${n} أشخاص` : `${n} شخصاً`) : `${n} ${n === 1 ? "guest" : "guests"}`;

export const fillTemplate = (body: string, b: Booking, lang: string) =>
  body.replace(/\{name\}/g, b.customer_name || "")
    .replace(/\{event\}/g, (lang === "ar" ? b.event_title_ar || b.venue_name_ar : b.event_title_en || b.venue_name_en) || "")
    .replace(/\{date\}/g, fmtDay(b.date, lang))
    .replace(/\{guests\}/g, guestsLabel(b.guests, lang));

export const relTime = (iso: string, lang: string) => {
  const s = Math.round((new Date(iso).getTime() - Date.now()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(lang === "ar" ? "ar" : "en", { numeric: "auto" });
  if (s > -60) return rtf.format(0, "second");
  if (s > -3600) return rtf.format(Math.round(s / 60), "minute");
  if (s > -86400) return rtf.format(Math.round(s / 3600), "hour");
  return fmtDateTime(iso, lang);
};

export const WaMenu = ({ b, templates, onContact, compact = false }: { b: Booking; templates: WaTemplate[]; onContact: (b: Booking, tpl: WaTemplate) => void; compact?: boolean }) => {
  const { t, lang } = usePrefs();
  const open = (tpl: WaTemplate) => {
    window.open(`https://wa.me/${waNumber(b.customer_phone || "")}?text=${encodeURIComponent(fillTemplate(tpl.body, b, lang))}`, "_blank", "noreferrer");
    onContact(b, tpl);
  };
  return (
    <DropdownMenu dir={lang === "ar" ? "rtl" : "ltr"}>
      <DropdownMenuTrigger asChild>
        <button data-testid={`venue-whatsapp-${b.booking_id}`} aria-label={t("b5.whatsapp")}
          className={`h-9 rounded-full text-xs font-bold inline-flex items-center justify-center gap-1.5 bg-[#25D366] text-white hover:bg-[#1ebe5b] active:scale-95 transition-transform shrink-0 ${compact ? "w-9" : "px-4"}`}>
          <MessageCircle className="h-4 w-4" />{!compact && <>{t("b5.whatsapp")}<ChevronDown className="h-3.5 w-3.5" /></>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64 rounded-2xl p-1.5">
        {templates.map((tpl) => (
          <DropdownMenuItem key={tpl.id} data-testid={`venue-whatsapp-tpl-${tpl.id}-${b.booking_id}`} onSelect={() => open(tpl)} className="rounded-xl flex-col items-start gap-0.5 py-2 cursor-pointer">
            <span className="font-bold text-sm">{tpl.title}</span>
            <span className="text-xs text-muted-foreground line-clamp-2">{fillTemplate(tpl.body, b, lang)}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const LastContactNote = ({ b }: { b: Booking }) => {
  const { t, lang } = usePrefs();
  const c = b.last_contact;
  if (!c) return null;
  return (
    <p data-testid={`venue-last-contact-${b.booking_id}`} className="inline-flex flex-wrap items-center gap-1 rounded-2xl bg-[#25D366]/10 text-emerald-700 dark:text-emerald-400 px-3 py-1 text-xs font-semibold">
      <History className="h-3.5 w-3.5" />
      {t("b5.lastContact").replace("{by}", c.by).replace("{when}", relTime(c.at, lang))}
      {c.template && <> · {c.template}</>}
      {(b.contact_count || 0) > 1 && <> · {(lang === "ar" && b.contact_count === 2 ? "مرتين" : t("b5.contactTimes").replace("{n}", String(b.contact_count)))}</>}
    </p>
  );
};
